import { Button, Modal } from '@orbind/ui'
import { useState, type CSSProperties } from 'react'
import { api } from '../../lib/api.ts'
import { useCustomFields, viewTypeDe } from '../admin/custom-field-data.ts'
import { useWiMutation } from './api.ts'
import { CatalogIcon } from '../../components/IconPicker.tsx'
import { CampoInfo } from './CampoInfo.tsx'
import {
  CampoInput,
  camposDoGrupo,
  filtrarVisiveis,
  rotuloCampo,
  visivelPorRegra,
  type ItemParaRegras,
} from './campos-shared.tsx'

/**
 * Tópicos com viewType 'form': aparecem como BOTÃO no topo da barra de
 * propriedades; ao clicar, abrem um modal em formato de formulário com os campos
 * do tópico e os botões Salvar / Descartar. Diferente dos outros modos, aqui os
 * valores só vão para o servidor ao clicar em Salvar (rascunho local).
 */
export function CamposComplementaresForm({
  workItemId,
  workItemTypeId,
  customValues,
  item,
  fieldHelp,
}: {
  workItemId: string
  workItemTypeId: string
  customValues: Record<string, unknown>
  item: ItemParaRegras
  /** Instrucoes personalizadas do item ({ chave: html }) — para o "i". */
  fieldHelp?: Record<string, string>
}) {
  const dados = useCustomFields('work_item')
  const [abertoId, setAbertoId] = useState<string | null>(null)
  const [rascunho, setRascunho] = useState<Record<string, unknown>>({})

  const salvar = useWiMutation(workItemId, async (novos: Record<string, unknown>) =>
    api['work-items'][':id'].$patch({ param: { id: workItemId }, query: {}, json: { customValues: novos } as never }),
  )

  const campos = (dados.data?.fields ?? []).filter((f) => f.defaultVisible)
  const grupos = dados.data?.groups ?? []
  const placements = dados.data?.placements ?? []
  const valores = customValues ?? {}

  // sem nenhum tipo marcado, o tópico não aparece em item algum (2026-08-29)
  const tipoPertence = (config: Record<string, unknown> | null | undefined) => {
    const tipos = config?.['workItemTypeIds']
    return Array.isArray(tipos) && tipos.includes(workItemTypeId)
  }
  const porGrupo = (gid: string) => filtrarVisiveis(camposDoGrupo(campos, placements, gid), item, valores)

  const forms = grupos.filter(
    (g) =>
      g.kind === 'topic' &&
      viewTypeDe(g.config) === 'form' &&
      tipoPertence(g.config) &&
      visivelPorRegra(g.config, item, valores) &&
      porGrupo(g.id).length > 0,
  )
  if (forms.length === 0) return null

  const iconeDe = (config: Record<string, unknown> | null | undefined) => (config?.['icon'] as string) || 'fas fa-folder'
  const abrir = (id: string) => {
    setRascunho({ ...valores })
    setAbertoId(id)
  }
  const grupoAberto = forms.find((g) => g.id === abertoId)
  const camposAbertos = grupoAberto ? porGrupo(grupoAberto.id) : []

  // largura do modal conforme os campos: quantos cabem lado a lado numa linha
  // (12 colunas) define quão largo vale abrir; ~300px por coluna para os campos
  // não ficarem apertados. O Modal já limita à tela.
  const menorLargura = camposAbertos.length ? Math.min(...camposAbertos.map((c) => c.width || 12)) : 12
  const porLinha = Math.max(1, Math.min(3, Math.floor(12 / Math.max(1, menorLargura))))
  const larguraModal = Math.min(920, Math.max(480, porLinha * 300 + 56))

  return (
    <div className="wi-cf__forms">
      {forms.map((g) => (
        <button key={g.id} type="button" className="wi-cf__formbtn" onClick={() => abrir(g.id)}>
          <CatalogIcon icon={iconeDe(g.config)} fallback="fas fa-folder" size={12} />
          <span className="wi-cf__formbtn-nome">{rotuloCampo(g.labels, 'Tópico')}</span>
        </button>
      ))}

      {grupoAberto ? (
        <Modal
          open
          width={larguraModal}
          resizable
          onClose={() => setAbertoId(null)}
          title={rotuloCampo(grupoAberto.labels, 'Tópico')}
          headerExtra={
            // "i" do topico em FORMULARIO: no cabecalho do modal (2026-09-01)
            <CampoInfo
              chave={`topic:${grupoAberto.id}`}
              titulo={rotuloCampo(grupoAberto.labels, 'Tópico')}
              ajudaPadrao={typeof grupoAberto.config?.['help'] === 'string' ? (grupoAberto.config['help'] as string) : null}
              workItemId={workItemId}
              fieldHelp={fieldHelp}
            />
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setAbertoId(null)}>
                Descartar
              </Button>
              <Button
                variant="primary"
                loading={salvar.isPending}
                onClick={() => salvar.mutate(rascunho, { onSuccess: () => setAbertoId(null) })}
              >
                Salvar
              </Button>
            </>
          }
        >
          {(grupoAberto.config?.['description'] as string) ? (
            <p className="wi-cf__abadesc" style={{ marginTop: 0 }}>
              {grupoAberto.config!['description'] as string}
            </p>
          ) : null}
          <div className="wi-cf__grid">
            {camposAbertos.map(({ campo: f, width }) => (
              <div key={f.id} className="wi-cf__campo" style={{ '--cf-w': width } as CSSProperties}>
                <label className="wi-cf__label">
                  {rotuloCampo(f.labels, f.key)}
                  {f.required ? <span className="wi-cf__obrig"> *</span> : null}
                  <CampoInfo
                    chave={f.key}
                    titulo={rotuloCampo(f.labels, f.key)}
                    ajudaPadrao={typeof f.config.help === 'string' ? f.config.help : null}
                    workItemId={workItemId}
                    fieldHelp={fieldHelp}
                  />
                </label>
                {/* rascunho local: grava só ao clicar em Salvar */}
                <CampoInput
                  f={f}
                  valores={rascunho}
                  setValores={setRascunho}
                  gravar={(key, valor) => setRascunho((s) => ({ ...s, [key]: valor }))}
                  escopo={workItemId}
                />
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
